<template>
	<div class="survey">
		<div class="survey-container">
			<div class="d-flex justify-space-between mb-7">
				<div>
					<h2 class="mr-5">Resan till och från skolan</h2>
					<p>– tack för att du bidrar!</p>
				</div>
          <div style="text-align: right; max-width: 80px; max-height: 80px;" v-if="endUser && endUser.client && endUser.client.logo">
            <img
              v-if="endUser && endUser.client && endUser.client.logo"
              style="max-width: 80px; max-height: 80px;"
              :src="apiUrl + '/storage/' + endUser.client.logo"
              :alt="$processText('{clientName} Logo')"
            />
				</div>
			</div>

			<!-- Page 1: BEHAVIOR POST INTERVENTION -->
			<div v-if="stepIndex === 0">
				<p>I denna enkät ställer vi frågor om resande till skolan och om Helsingborgs stads projekt kring detta under hösten. Vissa har redan gjort en liknande enkät. Vi ber er svara även på denna uppföljning.</p>
				<p>Vi behandlar och förvarar din svarsdata säkert och anonymiserat. Utöver nu 2023 kan den komma att användas även i framtida analyser. Läs mer om hur <a :href="$processText('https://data.nudgd.se/{slug}')" target="_blank">dina personuppgifter hanteras.</a>. Enkäten hanteras av företaget Nudgd på uppdrag av Helsingborgs stad.</p>

				<h2 class="mt-10 mb-2">Färdmedel till och från skolan</h2>
				<p class="mb-0"><em class="smaller-em">Fyll i det alternativ som passar bäst respektive uppskattningar som känns rimliga.</em></p>
				
				<h4 class="mt-3 mb-3" :class="{surveyError: errors.includes('mainModeOfTransport_POST')}">{{ $processText('{post-survey-main-travel-mode-after}') }}</h4>
				<ModeOfTransport :key="2" :value="mainModeOfTransport_POST" @selected="selectModeOfTransport" />

				<div class="nudgd-btn inline survey-submit mt-5" @click="confirmFirstStep()" v-if=" ! firstStepConfirmed">Gå vidare</div>
				<div style="clear:both;"></div>

				<template v-if="firstStepConfirmed">

					<h4 class="mt-2 mb-3">{{ $processText('{post-survey-travel-mode-days-per-week-after}') }}</h4>
					<p class="mb-2"><em class="smaller-em">(Används olika färdmedel för samma resa, räkna med alla)</em></p>
					<div class="survey-frequency-chips walk">
						<h3 :class="{surveyError: errors.includes('frequencyWalk_POST')}">🚶 Till fots</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip walking" :class="{active: frequencyWalk_POST == number}" v-for="number in [0, 1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('frequencyWalk_POST', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyBike_POST')}">🚲 Cykel/lådcykel</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip bike" :class="{active: frequencyBike_POST == number}" v-for="number in [0, 1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('frequencyBike_POST', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyTransit_POST')}">🚌 Kollektivt</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip transit" :class="{active: frequencyTransit_POST == number}" v-for="number in [0, 1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('frequencyTransit_POST', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyCar_POST')}">🚗 {{ $processText('{post-survey-travel-mode-days-per-week-car-own-after}') }}</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip car" :class="{active: frequencyCar_POST == number}" v-for="number in [0, 1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('frequencyCar_POST', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyCarCoride_POST')}">🚙 Bil – samåkning med andra</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip car" :class="{active: frequencyCarCoride_POST == number}" v-for="number in [0, 1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('frequencyCarCoride_POST', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyMisc_POST')}">Annat</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip" :class="{active: frequencyMisc_POST == number}" v-for="number in [0, 1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('frequencyMisc_POST', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>

					<template v-if="frequencyWalk_POST || frequencyBike_POST || frequencyTransit_POST || frequencyCar_POST || frequencyCarCoride_POST || frequencyMisc_POST">
						<h4 class="mt-2 mb-1">{{ $processText('{post-survey-average-number-weekly-trips-after}') }}</h4>
						<p class="mb-2"><em class="smaller-em">{{ $processText('{post-survey-average-number-weekly-trips-after-help}') }}</em></p>

						<div class="survey-thirds mt-3">
							<div class="survey-thirds-first" :class="{surveyError: errors.includes('numberOfTrips_POST')}">Antal enkelresor per vecka:</div>

							<div class="survey-thirds-middle">
								<vue-slider dotSize="20" :max="35" v-model="numberOfTrips_POST"></vue-slider>
							</div>

							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="numberOfTrips_POST"></v-text-field> resor</div>
						</div>

						<h4 class="mt-6 mb-6">{{ $processText('{post-survey-average-weekly-distance-per-travel-mode-after}') }}</h4>
						<p class="mb-2"><em class="smaller-em">(Räkna med både vägen till och från skolan/skolorna, inklusive eventuella ärenden på vägen. Du kan antingen dra i ikonen eller skriva in avståndet direkt i kolumnen till höger.)</em></p>
						<div class="survey-thirds walking mt-3" v-if="frequencyWalk_POST > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyWalking_POST')}">Till fots</div>
							<div class="survey-thirds-middle"><vue-slider :max="200" v-model="weeklyWalking_POST"><template v-slot:dot><div class="custom-dot alt">🚶</div></template></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyWalking_POST"></v-text-field> km</div>
						</div>

						<div class="survey-thirds bike mt-3" v-if="frequencyBike_POST > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyBike_POST')}">Cykel/lådcykel</div>
							<div class="survey-thirds-middle"><vue-slider :max="200" v-model="weeklyBike_POST"><template v-slot:dot><div class="custom-dot alt">🚲</div></template></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyBike_POST"></v-text-field> km</div>
						</div>

						<div class="survey-thirds transit mt-3" v-if="frequencyTransit_POST > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyTransit_POST')}">Kollektivt</div>
							<div class="survey-thirds-middle"><vue-slider :max="1000" v-model="weeklyTransit_POST"><template v-slot:dot><div class="custom-dot alt">🚌</div></template></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyTransit_POST"></v-text-field> km</div>
						</div>

						<div class="survey-thirds car mt-3" v-if="frequencyCar_POST > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyCar_POST')}">{{ $processText('{post-survey-travel-mode-days-per-week-car-own-after}') }}</div>
							<div class="survey-thirds-middle"><vue-slider :max="1000" v-model="weeklyCar_POST"><template v-slot:dot><div class="custom-dot alt">🚗</div></template></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyCar_POST"></v-text-field> km</div>
						</div>

						<div class="survey-thirds car carCoride mt-3" v-if="frequencyCarCoride_POST > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyCarCoride_POST')}">Bil - samåkning med annan</div>
							<div class="survey-thirds-middle"><vue-slider :max="1000" v-model="weeklyCarCoride_POST"><template v-slot:dot><div class="custom-dot alt">🚙</div></template></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyCarCoride_POST"></v-text-field> km</div>
						</div>

						<div class="survey-thirds misc mt-3" v-if="frequencyMisc_POST > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyMisc_POST')}">
								<v-tooltip bottom>
							      <template v-slot:activator="{ on, attrs }"><span v-bind="attrs" v-on="on">Annat <div class="tooltip-container"><span class="icon-info"></span></div></span></template>
							      <span>t.ex. elsparkcykel, skateboard etc."</span>
							    </v-tooltip>
							</div>
							<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="200" v-model="weeklyMisc_POST"></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyMisc_POST"></v-text-field> km</div>
						</div>

						<div class="survey-thirds-sum"><strong>SUMMA</strong> <span>{{ weeklySum_POST }} km</span></div>

						<div class="survey-sum mt-6">
							Uppgifterna du angett ovan motsvarar <strong>{{ yearlyDistance_POST }} km</strong> utslaget på ett år.
							<br/>
							<br/>
							<em>Om reslängden per vecka verkar rimlig, vänligen gå vidare i enkäten. Om inte, justera i frågorna ovan.</em></div>


						<div v-if="weeklyCar_POST">
							<h4 class="mt-5">Hur är bilresorna fördelade utifrån drivmedel?</h4>
							<p class="mb-2"><em class="smaller-em">(Summan för de olika drivmedlen måste matcha med Bil - själv eller med hushållsmedlem ovan <strong>({{ weeklyCar_POST }} km)</strong>. Du kan antingen dra i ikonen eller skriva in avståndet direkt i kolumnen till höger.)</em></p>

							<div class="survey-thirds electricity mt-3">
								<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyElectricity_POST')}">El</div>
								<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="1000" v-model="weeklyElectricity_POST"></vue-slider></div>
								<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyElectricity_POST"></v-text-field> km</div>
							</div>

							<div class="survey-thirds hybrid mt-3">
								<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyHybrid_POST')}">Hybrid/gas</div>
								<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="1000" v-model="weeklyHybrid_POST"></vue-slider></div>
								<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyHybrid_POST"></v-text-field> km</div>
							</div>

							<div class="survey-thirds gas mt-3">
								<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyGas_POST')}">Bensin/diesel<br/>/etanol</div>
								<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="1000" v-model="weeklyGas_POST"></vue-slider></div>
								<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyGas_POST"></v-text-field> km</div>
							</div>
						</div>
					</template>

					<h4 class="mt-5 mb-2">{{ $processText('{post-survey-other-travel-types-after}') }}</h4>
					<v-textarea class="mt-4" rows="4" hide-details solo v-model="otherTravelTypes_POST" placeholder="T.ex: Annat färdmedel till jobbet/fritidsaktivitet? Färre/fler resor till mataffären? Reser nu/inte längre ihop med andra? Skriv också gärna vad som ledde till förändringen"></v-textarea>

					<div class="nudgd-btn inline survey-submit mt-10" @click="nextStep()">Gå vidare</div>
					<div style="clear:both;"></div>
				</template>
			</div>

			<!-- Page 2: BEHAVIOR PRE INTERVENTION -->
			<!-- 
				LEO: Logic needed to only show page 2 for unknown end users 
				(the ones we can't match to pre-survey replies (eg endusers that did not clicked 
				a) specific link with slug, or 
				b) got the slug via a cookie)) 					
			-->

			<div v-if="stepIndex === 1 && isNewUser">
				<h2 class="mt-10 mb-2">Färdmedel till och från skolan tidigare</h2>
				<p class="mb-0"><em class="smaller-em">Fyll i det alternativ som passar bäst respektive uppskattningar som känns rimliga.</em></p>
				
				<h4 class="mt-3 mb-3" :class="{surveyError: errors.includes('mainModeOfTransport_PRE')}">{{ $processText('{post-survey-main-travel-mode-before}') }}</h4>
				<ModeOfTransport :key="1" :value="mainModeOfTransport_PRE" @selected="selectModeOfTransportPRE" />

				<div class="nudgd-btn inline survey-submit mt-5" @click="confirmFirstStepPre()" v-if=" ! firstStepConfirmedPre">Gå vidare</div>
				<div class="nudgd-btn small survey-submit mt-5" @click="prevStep()">Tillbaka</div>
				<div style="clear:both;"></div>

				<template v-if="firstStepConfirmedPre">

					<h4 class="mt-2 mb-3">{{ $processText('{post-survey-travel-mode-days-per-week-before}') }}</h4>
					<p class="mb-2"><em class="smaller-em">(Används olika färdmedel för samma resa, räkna med alla)</em></p>
					<div class="survey-frequency-chips walk">
						<h3 :class="{surveyError: errors.includes('frequencyWalk_PRE')}">🚶 Till fots</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip walking" :class="{active: frequencyWalk_PRE == number}" v-for="number in [0, 1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('frequencyWalk_PRE', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyBike_PRE')}">🚲 Cykel/lådcykel</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip bike" :class="{active: frequencyBike_PRE == number}" v-for="number in [0, 1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('frequencyBike_PRE', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyTransit_PRE')}">🚌 Kollektivt</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip transit" :class="{active: frequencyTransit_PRE == number}" v-for="number in [0, 1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('frequencyTransit_PRE', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyCar_PRE')}">{{ $processText('{post-survey-travel-mode-days-per-week-car-own-before}') }}</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip car" :class="{active: frequencyCar_PRE == number}" v-for="number in [0, 1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('frequencyCar_PRE', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyCarCoride_PRE')}">🚙 Bil – samåkning med andra</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip car" :class="{active: frequencyCarCoride_PRE == number}" v-for="number in [0, 1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('frequencyCarCoride_PRE', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyMisc_PRE')}">Annat</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip" :class="{active: frequencyMisc_PRE == number}" v-for="number in [0, 1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('frequencyMisc_PRE', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>

					<template v-if="frequencyWalk_PRE || frequencyBike_PRE || frequencyTransit_PRE || frequencyCar_PRE || frequencyCarCoride_PRE || frequencyMisc_PRE">
						<h4 class="mt-2 mb-1">{{ $processText('{post-survey-average-number-weekly-trips-before}') }}</h4>
						<p class="mb-2"><em class="smaller-em">(Tur och retur räknas som två enkelresor. Exempel: Två barn som färdas till och från skolan fem dagar i veckan blir 20 enkelresor per vecka.)</em></p>

						<div class="survey-thirds mt-3">
							<div class="survey-thirds-first" :class="{surveyError: errors.includes('numberOfTrips_PRE')}">Antal enkelresor per vecka:</div>

							<div class="survey-thirds-middle">
								<vue-slider dotSize="20" :max="35" v-model="numberOfTrips_PRE"></vue-slider>
							</div>

							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="numberOfTrips_PRE"></v-text-field> resor</div>
						</div>

						<h4 class="mt-6 mb-6">{{ $processText('{post-survey-average-weekly-distance-per-travel-mode-before}') }}</h4>
						<p class="mb-2"><em class="smaller-em">(Räkna med både vägen till och från skolan/skolorna, inklusive eventuella ärenden på vägen. Du kan antingen dra i ikonen eller skriva in avståndet direkt i kolumnen till höger.)</em></p>
						<div class="survey-thirds walking mt-3" v-if="frequencyWalk_PRE > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyWalking_PRE')}">Till fots</div>
							<div class="survey-thirds-middle"><vue-slider :max="200" v-model="weeklyWalking_PRE"><template v-slot:dot><div class="custom-dot alt">🚶</div></template></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyWalking_PRE"></v-text-field> km</div>
						</div>

						<div class="survey-thirds bike mt-3" v-if="frequencyBike_PRE > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyBike_PRE')}">Cykel/lådcykel</div>
							<div class="survey-thirds-middle"><vue-slider :max="200" v-model="weeklyBike_PRE"><template v-slot:dot><div class="custom-dot alt">🚲</div></template></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyBike_PRE"></v-text-field> km</div>
						</div>

						<div class="survey-thirds transit mt-3" v-if="frequencyTransit_PRE > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyTransit_PRE')}">Kollektivt</div>
							<div class="survey-thirds-middle"><vue-slider :max="1000" v-model="weeklyTransit_PRE"><template v-slot:dot><div class="custom-dot alt">🚌</div></template></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyTransit_PRE"></v-text-field> km</div>
						</div>

						<div class="survey-thirds car mt-3" v-if="frequencyCar_PRE > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyCar_PRE')}">{{ $processText('{post-survey-travel-mode-days-per-week-car-own-before}') }}</div>
							<div class="survey-thirds-middle"><vue-slider :max="1000" v-model="weeklyCar_PRE"><template v-slot:dot><div class="custom-dot alt">🚗</div></template></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyCar_PRE"></v-text-field> km</div>
						</div>

						<div class="survey-thirds car carCoride mt-3" v-if="frequencyCarCoride_PRE > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyCarCoride_PRE')}">Bil - samåkning med annan</div>
							<div class="survey-thirds-middle"><vue-slider :max="1000" v-model="weeklyCarCoride_PRE"><template v-slot:dot><div class="custom-dot alt">🚙</div></template></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyCarCoride_PRE"></v-text-field> km</div>
						</div>

						<div class="survey-thirds misc mt-3" v-if="frequencyMisc_PRE > 0">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyMisc_PRE')}">
								<v-tooltip bottom>
							      <template v-slot:activator="{ on, attrs }"><span v-bind="attrs" v-on="on">Annat <div class="tooltip-container"><span class="icon-info"></span></div></span></template>
							      <span>t.ex. elsparkcykel, skateboard etc."</span>
							    </v-tooltip>
							</div>
							<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="200" v-model="weeklyMisc_PRE"></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyMisc_PRE"></v-text-field> km</div>
						</div>

						<div class="survey-thirds-sum"><strong>SUMMA</strong> <span>{{ weeklySum_PRE }} km</span></div>

						<div class="survey-sum mt-6">
							Uppgifterna du angett ovan motsvarar <strong>{{ yearlyDistance_PRE }} km</strong> utslaget på ett år.
							<br/>
							<br/>
							<em>Om reslängden per vecka verkar rimlig, vänligen gå vidare i enkäten. Om inte, justera i frågorna ovan.</em></div>


						<div v-if="weeklyCar_PRE">
							<h4 class="mt-5">Hur är bilresorna fördelade utifrån drivmedel?</h4>
							<p class="mb-2"><em class="smaller-em">(Summan för de olika drivmedlen måste matcha med Bil - själv eller med hushållsmedlem ovan <strong>({{ weeklyCar_PRE }} km)</strong>. Du kan antingen dra i ikonen eller skriva in avståndet direkt i kolumnen till höger.)</em></p>

							<div class="survey-thirds electricity mt-3">
								<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyElectricity_PRE')}">El</div>
								<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="1000" v-model="weeklyElectricity_PRE"></vue-slider></div>
								<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyElectricity_PRE"></v-text-field> km</div>
							</div>

							<div class="survey-thirds hybrid mt-3">
								<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyHybrid_PRE')}">Hybrid/gas</div>
								<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="1000" v-model="weeklyHybrid_PRE"></vue-slider></div>
								<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyHybrid_PRE"></v-text-field> km</div>
							</div>

							<div class="survey-thirds gas mt-3">
								<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyGas_PRE')}">Bensin/diesel<br/>/etanol</div>
								<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="1000" v-model="weeklyGas_PRE"></vue-slider></div>
								<div class="survey-thirds-last"><v-text-field placeholder="0" v-model="weeklyGas_PRE"></v-text-field> km</div>
							</div>
						</div>
					</template>

					<div class="nudgd-btn inline survey-submit mt-10" @click="nextStep()">Gå vidare</div>
					<div class="nudgd-btn small survey-submit mt-10" @click="prevStep()">Tillbaka</div>
					<div style="clear:both;"></div>
				</template>
			</div>

			<!-- Page 3: MOTIVATION / ATTITUDES					-->
			<div v-if="stepIndex === 2">
				<h4 class="mt-6 mb-2">{{ $processText('{post-survey-feeling-about-increase-use}') }}</h4>
				
				<div class="survey-frequency-chips">
					<h3 :class="{surveyError: errors.includes('wishedIncreaseModeOfTransportWalk')}">🚶 Till fots</h3>
					<div class="survey-frequency-chips-container">
						<div class="survey-frequency-chips-container-chip walking" :class="{active: wishedIncreaseModeOfTransportWalk == number}" v-for="number in [1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('wishedIncreaseModeOfTransportWalk', number)">{{ number }}</div>
					</div>
					<div class="survey-frequency-chips-helpers" style="color:#000;">
						<div>Mycket<br>negativt<br>inställd</div>
						<div>Neutral</div>
						<div class="text-right">Mycket<br>positivt<br>inställd</div>
					</div>
				</div>
				<div class="survey-frequency-chips mt-7">
					<h3 :class="{surveyError: errors.includes('wishedIncreaseModeOfTransportBike')}">🚲 Cykel</h3>
					<div class="survey-frequency-chips-container">
						<div class="survey-frequency-chips-container-chip bike" :class="{active: wishedIncreaseModeOfTransportBike == number}" v-for="number in [1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('wishedIncreaseModeOfTransportBike', number)">{{ number }}</div>
					</div>
					<div class="survey-frequency-chips-helpers" style="color:#000;">
						<div>Mycket<br>negativt<br>inställd</div>
						<div>Neutral</div>
						<div class="text-right">Mycket<br>positivt<br>inställd</div>
					</div>
				</div>
				<div class="survey-frequency-chips mt-7">
					<h3 :class="{surveyError: errors.includes('wishedIncreaseModeOfTransportTransit')}">🚌 Kollektivtrafik</h3>
					<div class="survey-frequency-chips-container">
						<div class="survey-frequency-chips-container-chip transit" :class="{active: wishedIncreaseModeOfTransportTransit == number}" v-for="number in [1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('wishedIncreaseModeOfTransportTransit', number)">{{ number }}</div>
					</div>
					<div class="survey-frequency-chips-helpers" style="color:#000;">
						<div>Mycket<br>negativt<br>inställd</div>
						<div>Neutral</div>
						<div class="text-right">Mycket<br>positivt<br>inställd</div>
					</div>
				</div>
				<div class="survey-frequency-chips mt-7">
					<h3 :class="{surveyError: errors.includes('wishedIncreaseModeOfTransportCar')}">🚗 {{ $processText('{post-survey-feeling-about-increase-use-after-old-car}') }}</h3>
					<div class="survey-frequency-chips-container">
						<div class="survey-frequency-chips-container-chip car" :class="{active: wishedIncreaseModeOfTransportCar == number}" v-for="number in [1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('wishedIncreaseModeOfTransportCar', number)">{{ number }}</div>
					</div>
					<div class="survey-frequency-chips-helpers" style="color:#000;">
						<div>Mycket<br>negativt<br>inställd</div>
						<div>Neutral</div>
						<div class="text-right">Mycket<br>positivt<br>inställd</div>
					</div>
				</div>
				<div class="survey-frequency-chips mt-7">
					<h3 :class="{surveyError: errors.includes('wishedIncreaseModeOfTransportCarSustainable')}">🚗 {{ $processText('{post-survey-feeling-about-increase-use-after-modern-car}') }}</h3>
					<div class="survey-frequency-chips-container">
						<div class="survey-frequency-chips-container-chip car" :class="{active: wishedIncreaseModeOfTransportCarSustainable == number}" v-for="number in [1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('wishedIncreaseModeOfTransportCarSustainable', number)">{{ number }}</div>
					</div>
					<div class="survey-frequency-chips-helpers" style="color:#000;">
						<div>Mycket<br>negativt<br>inställd</div>
						<div>Neutral</div>
						<div class="text-right">Mycket<br>positivt<br>inställd</div>
					</div>
				</div>
				<div class="survey-frequency-chips mt-7">
					<h3 :class="{surveyError: errors.includes('wishedIncreaseModeOfTransportCoride')}">🚙 Samåkning</h3>
					<div class="survey-frequency-chips-container">
						<div class="survey-frequency-chips-container-chip car" :class="{active: wishedIncreaseModeOfTransportCoride == number}" v-for="number in [1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('wishedIncreaseModeOfTransportCoride', number)">{{ number }}</div>
					</div>
					<div class="survey-frequency-chips-helpers" style="color:#000;">
						<div>Mycket<br>negativt<br>inställd</div>
						<div>Neutral</div>
						<div class="text-right">Mycket<br>positivt<br>inställd</div>
					</div>
				</div>



				<h4 class="mt-6 mb-2">{{ $processText('{post-survey-feeling-about-increasing-use-others}') }}</h4>
				
				<div class="survey-frequency-chips">
					<h3 :class="{surveyError: errors.includes('othersWishedIncreaseModeOfTransportWalk')}">🚶 Till fots</h3>
					<div class="survey-frequency-chips-container">
						<div class="survey-frequency-chips-container-chip walking" :class="{active: othersWishedIncreaseModeOfTransportWalk == number}" v-for="number in [1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('othersWishedIncreaseModeOfTransportWalk', number)">{{ number }}</div>
					</div>
					<div class="survey-frequency-chips-helpers" style="color:#000;">
						<div>Mycket<br>negativt<br>inställd</div>
						<div>Neutral</div>
						<div class="text-right">Mycket<br>positivt<br>inställd</div>
					</div>
				</div>
				<div class="survey-frequency-chips mt-7">
					<h3 :class="{surveyError: errors.includes('othersWishedIncreaseModeOfTransportBike')}">🚲 Cykel</h3>
					<div class="survey-frequency-chips-container">
						<div class="survey-frequency-chips-container-chip bike" :class="{active: othersWishedIncreaseModeOfTransportBike == number}" v-for="number in [1, 2, 3, 4, 5]" :key="number" @click="selectFrequency('othersWishedIncreaseModeOfTransportBike', number)">{{ number }}</div>
					</div>
					<div class="survey-frequency-chips-helpers" style="color:#000;">
						<div>Mycket<br>negativt<br>inställd</div>
						<div>Neutral</div>
						<div class="text-right">Mycket<br>positivt<br>inställd</div>
					</div>
				</div>

				<h4 class="mt-6 mb-2" :class="{surveyError: errors.includes('reasonForTransport_POST')}">I valet av färdsätt till och från skolan väger detta tyngst</h4>
				<p class="mb-2"><em class="smaller-em">Ange ett eller två alternativ</em></p>

				<div class="chip-rows">
					<v-chip-group active-class="primary--text" column multiple v-model="reasonForTransport_POST" max="2">
					<v-chip value="Att resa så som kollegor/vänner/andra föräldrar gör" outlined>Att resa så som kollegor/vänner/andra föräldrar gör</v-chip>
					<v-chip value="Bekvämlighet" outlined>Bekvämlighet</v-chip>
					<v-chip value="Hälsa" outlined>Hälsa</v-chip>
					<v-chip value="Kostnad" outlined>Kostnad</v-chip>
					<v-chip value="Miljö" outlined>Miljö</v-chip>
					<v-chip value="Säkerhet" outlined>Säkerhet</v-chip>
				    <v-chip value="Tid" outlined>Tid</v-chip>
				</v-chip-group>
				</div>

				<h4 class="mt-5 mb-2">Kommentar</h4>
				<v-textarea class="mt-4" rows="4" hide-details solo v-model="reasonForTransportComment_POST" placeholder="Lägg gärna till något om vad som påverkar hur du tänker med resor till och från skolan framöver, och varför – vi vill höra vad du tycker!"></v-textarea>

				<div class="nudgd-btn inline survey-submit mt-10"  @click="nextStep()">Gå vidare</div>
				<div class="nudgd-btn small survey-submit mt-10" @click="prevStep()">Tillbaka</div>
				<div style="clear:both;"></div>
			</div>


<!-- Page 4: SMART NUDGES MOBILITY USE 					-->
            <div v-if="stepIndex === 3">
              <img
                v-if="!serviceImage"
                style="float: right;"
                width="300"
                :src="$apiUrl + '/storage/google-pixel-2-xl-resvana-20-1280x720-umea-1625230507wJMF2.png'"
                alt="Tjänsten Resvana"
              />
              <img
                v-if="serviceImage"
                style="float: right;"
                width="300"
                :src="$apiUrl + '/storage/' + serviceImage"
                alt="Tjänsten Resvana"
              />
				<div class="mt-2 mb-4" :class="{surveyError: errors.includes('usedSmartNudges')}"><strong>Du blev i oktober inbjuden av rektorn till en plattform om resande till skolan.</strong> Projektet syftar till att uppmuntra färd till skolan med aktivt resande (promenad, cykel etc.) istället för med bil. <strong>Gick du in på länken och tog del av innehållet?</strong></div>

				<div class="survey-radio-row mb-6">
					<v-radio-group class="mt-0" v-model="usedSmartNudges" row>
						<v-radio label="Ja" value="Ja"></v-radio>
						<v-radio label="Nej" value="Nej"></v-radio>
					</v-radio-group>
				</div>

				<div v-if="usedSmartNudges === 'Ja'">
					<h4 class="mt-4" :class="{surveyError: errors.includes('smartNudgesEffect')}">{{ $processText('{post-survey-platform-contribution-score}') }}</h4>
					<em class="smaller-em">(0 = Inte alls; 10 = Helt och hållet)</em>
					<vue-slider marks class="mt-3" :max="10" v-model="smartNudgesEffect"></vue-slider>


					<h4 class="mt-10 mb-4">{{ $processText('{post-survey-platform-contribution-travel-mode}') }}</h4>

					<div class="survey-radio-row">
						<div class="survey-radio-row-first" :class="{surveyError: errors.includes('moreWalking')}">...promenerera (eller åka rullstol) mer</div>
						<v-radio-group class="mt-0" v-model="more.walking" row>
							<v-radio label="Ja" value="Ja"></v-radio>
							<v-radio label="Nja: planerar att göra det" value="Nja: planerar att göra det"></v-radio>
							<v-radio label="Nej" value="Nej"></v-radio>
						</v-radio-group>
					</div>

					<div class="survey-radio-row">
						<div class="survey-radio-row-first" :class="{surveyError: errors.includes('moreBiking')}">...cykla mer</div>
						<v-radio-group v-model="more.biking" row>
							<v-radio label="Ja" value="Ja"></v-radio>
							<v-radio label="Nja: planerar att göra det" value="Nja: planerar att göra det"></v-radio>
							<v-radio label="Nej" value="Nej"></v-radio>
						</v-radio-group>
					</div>

					<div class="survey-radio-row">
						<div class="survey-radio-row-first" :class="{surveyError: errors.includes('moreTransit')}">...åka mer kollektivtrafik (av någon typ)</div>
						<v-radio-group v-model="more.transit" row>
							<v-radio label="Ja" value="Ja"></v-radio>
							<v-radio label="Nja: planerar att göra det" value="Nja: planerar att göra det"></v-radio>
							<v-radio label="Nej" value="Nej"></v-radio>
						</v-radio-group>
					</div>

					<div class="survey-radio-row">
						<div class="survey-radio-row-first" :class="{surveyError: errors.includes('moreCar')}">...åka bil på ett mer hållbart sätt, t.ex. elbil, samåkning, bilpool, etc.</div>
							<v-radio-group v-model="more.car" row>
								<v-radio label="Ja" value="Ja"></v-radio>
							<v-radio label="Nja: planerar att göra det" value="Nja: planerar att göra det"></v-radio>
								<v-radio label="Nej" value="Nej"></v-radio>
							</v-radio-group>
					</div>

					<h4 class="mt-3 mb-3" :class="{surveyError: errors.includes('usefulPartsOfPlatform')}">Vilka delar av plattformen var användbara?</h4>
					<p class="mb-2"><em class="smaller-em">(Du kan ange flera alternativ)</em></p>
					<div class="chip-rows">
						<v-chip-group active-class="primary--text" column multiple v-model="usefulPartsOfPlatform" max="7">
							<v-chip value="Texterna & länkarna om resande" outlined>Texterna & länkarna om resande</v-chip>
							<v-chip value="Texterna om nya vanor" outlined>Texterna om nya vanor</v-chip>
							<v-chip value="Jämförelsen av olika färdmedel" outlined>Jämförelsen av olika färdmedel</v-chip>
							<v-chip value="Kartan" outlined>Kartan</v-chip>
							<v-chip value="Utmaningarna" outlined>Utmaningarna</v-chip>
							<v-chip value="Quizen" outlined>Quizen</v-chip>
							<v-chip value="Annat" outlined>Annat (vänligen ange i kommentar nedan)</v-chip>
							<v-chip value="Inget" outlined>Inget</v-chip>
						</v-chip-group>
					</div>

					<h4 class="mb-3 mt-10">Kommentar – utveckla gärna!<span style="font-weight:normal;"> (ej obligatorisk)</span></h4>
					<v-textarea rows="4" hide-details solo v-model="usefulPartsOfPlatformCommentary"></v-textarea>
					<div class="survey-help mt-2">(T.ex. på vilket sätt? Vad specifikt? Något annat som var hjälpsamt? Eller varför var något inte användbart? - Vänligen lämna inga personuppgifter i detta fält)</div>

					<h4 class="mb-3 mt-6" :class="{surveyError: errors.includes('smartNudgesRecommend')}">Hur troligt är det att du skulle rekommendera plattformen till en kollega eller vän?</h4>
					<em class="smaller-em">(0 = Inte alls troligt; 10 = Extremt troligt)</em>
					<vue-slider marks class="pb-10" :max="10" v-model="smartNudgesRecommend"></vue-slider>
				</div>

				<div v-if="usedSmartNudges === 'Nej'">
					<h4 class="mb-3 mt-10">Varför använde du inte plattformen och vad hade kunnat få dig att göra det?<span style="font-weight:normal;"> (ej obligatorisk)</span></h4>
					<v-textarea rows="4" hide-details solo v-model="whyNotUsedPlatformCommentary" placeholder="T.ex: Berodde det på något kring plattformen eller projektet i sig eller var det något externt?"></v-textarea>
					<div class="survey-help mt-2">(Vänligen utveckla – vi vore väldigt tacksamma för din återkoppling! - Vänligen lämna inga personuppgifter i detta fält)</div>

					<h4 class="mt-3 mb-3" :class="{surveyError: errors.includes('whyNotUsedPlatform')}">Fortsättning på förra frågan: Spelade det någon eller några av dessa följande faktorer in?</h4>
					<p class="mb-2"><em class="smaller-em">(Du kan ange flera alternativ. När du har svarat här får du jättegärna gå tillbaka till förra frågan och utveckla ditt svar.)</em></p>
					<div class="chip-rows">
						<v-chip-group active-class="primary--text" column multiple v-model="whyNotUsedPlatform" max="15">
							<v-chip value="Såg inte meddelandena" outlined>Såg inte meddelandena</v-chip>
							<v-chip value="Meddelandena övertygade mig inte att klicka" outlined>Meddelandena övertygade mig inte att klicka</v-chip>
							<v-chip value="Hade inte tid" outlined>Hade inte tid</v-chip>
							<v-chip value="Ovillig att klicka på länken (cybersäkerhet)" outlined>Ovillig att klicka på länken (cybersäkerhet)</v-chip>
							<v-chip value="Ovillig, är nöjd med resvanorna" outlined>Ovillig, är nöjd med resvanorna</v-chip>
							<v-chip value="Ej relevant, går/cyklar redan" outlined>Ej relevant, går/cyklar redan</v-chip>
							<v-chip value="Ej relevant, för otryggt att gå/cykla" outlined>Ej relevant, för otryggt att gå/cykla</v-chip>
							<v-chip value="Ej relevant, fel årstid" outlined>Ej relevant, fel årstid</v-chip>
							<v-chip value="Otillräcklig tillgång till cykel" outlined>Otillräcklig tillgång till cykel</v-chip>
							<v-chip value="Meddelandenas tajming, veckodag" outlined>Meddelandenas tajming, veckodag</v-chip>
							<v-chip value="Meddelandenas tajming, tid på dygnet" outlined>Meddelandenas tajming, tid på dygnet</v-chip>
							<v-chip value="Tröttnade på att svara på frågor" outlined>Tröttnade på att svara på frågor</v-chip>
							<v-chip value="Ville klicka men glömde sen" outlined>Ville klicka men glömde sen</v-chip>
							<v-chip value="Klickade men glömde sen" outlined>Klickade men glömde sen</v-chip>
							<v-chip value="Annan anledning" outlined>Annan anledning (vänligen ange i förra frågan)</v-chip>
						</v-chip-group>
					</div>                    
				</div>

				<div class="nudgd-btn inline survey-submit mt-10" @click="nextStep()">Gå vidare</div>
				<div class="nudgd-btn small survey-submit mt-10" @click="prevStep()">Tillbaka</div>
				<div style="clear:both;"></div>
               
            </div>

			<!-- Page 5: DEMOGRAPHICS -->
			<div v-if="stepIndex === 4">

				<div v-if="isNewUser">
					<div v-if=" ! isStaff">
						<h4 class="mb-2" :class="{surveyError: errors.includes('worksInSchool')}">Jobbar du på en grundskola i kommunen (antingen kommunal eller privat)?</h4>
						<v-chip-group active-class="primary--text" column v-model="worksInSchool">
							<v-chip value="Ja" outlined>Ja</v-chip>
							<v-chip value="Nej" outlined>Nej</v-chip>
						</v-chip-group>
					</div>

					<div v-if="isStaff">
						<h4 class="mb-2" :class="{surveyError: errors.includes('hasChildrenInSchool')}">Har du barn som går på en av Helsingborgs stads grundskolor (antingen kommunal eller privat)?</h4>
						<v-chip-group active-class="primary--text" column v-model="hasChildrenInSchool">
							<v-chip value="Ja" outlined>Ja</v-chip>
							<v-chip value="Nej" outlined>Nej</v-chip>
						</v-chip-group>
					</div>
				</div>

        <h4 class="mb-2" :class="{surveyError: errors.includes('gender')}">Kön</h4>
        <v-chip-group active-class="primary--text" column v-model="gender">
            <v-chip value="Man" outlined>Man</v-chip>
            <v-chip value="Kvinna" outlined>Kvinna</v-chip>
            <v-chip value="Annat/Vill ej ange" outlined>Annat/Vill ej ange</v-chip>
        </v-chip-group>

        <h4 class="mb-2" :class="{surveyError: errors.includes('birthYear')}">Födelseår (YYYY)</h4>
        <v-text-field dense v-model="birthYear" outlined hide-details></v-text-field>

        <h4 class="mb-2" :class="{surveyError: errors.includes('peopleInHousehold')}">Antal personer i hushållet</h4>
        <v-chip-group active-class="primary--text" column v-model="peopleInHousehold">
            <v-chip value="1" outlined>1</v-chip>
            <v-chip value="2" outlined>2</v-chip>
            <v-chip value="3" outlined>3</v-chip>
            <v-chip value="4" outlined>4</v-chip>
            <v-chip value="5" outlined>5</v-chip>
            <v-chip value="5+" outlined>5+</v-chip>
        </v-chip-group>

		<div v-if="isStaff">
			<h4 class="mb-2" :class="{surveyError: errors.includes('noOfChildren')}">Barn i hushållet</h4>
			<v-chip-group active-class="primary--text" column v-model="noOfChildren">
			<v-chip value="Ja, upp till 12 år" outlined>Ja, upp till 12 år</v-chip>
			<v-chip value="Ja, tonåring(ar)" outlined>Ja, tonåring(ar)</v-chip>
				<v-chip value="Ja, både upp till 12 år och tonåring(ar)" outlined>Ja, både upp till 12 år och tonåring(ar)</v-chip>
				<v-chip value="Inga barn i hushållet" outlined>Inga barn i hushållet</v-chip>
			</v-chip-group>
		</div>

		<div v-if="isParent">
			<h4 class="mb-2" :class="{surveyError: errors.includes('employment')}">Sysselsättning</h4>
			<v-chip-group active-class="primary--text" column v-model="employment">
				<v-chip value="Arbetar" outlined>Arbetar</v-chip>
				<v-chip value="Studerar" outlined>Studerar</v-chip>
				<v-chip value="Annat" outlined>Annat</v-chip>
			</v-chip-group>
		</div>

        <h4 class="mb-2" :class="{surveyError: errors.includes('distanceEmployment')}">Distansarbete</h4>
        <p class="mb-2"><em class="smaller-em">I hur stor grad arbetar du på distans, t.ex. hemifrån?</em></p>
        <v-chip-group active-class="primary--text" column v-model="distanceEmployment">
            <v-chip value="0%" outlined>0%</v-chip>
            <v-chip value="20%" outlined>20%</v-chip>
            <v-chip value="40%" outlined>40%</v-chip>
            <v-chip value="60%" outlined>60%</v-chip>
            <v-chip value="80%" outlined>80%</v-chip>
            <v-chip value="100%" outlined>100%</v-chip>
        </v-chip-group>

        <h4 class="mb-2" :class="{surveyError: errors.includes('educationAttainment')}">Utbildningsnivå</h4>
        <p class="mb-2"><em class="smaller-em">(Markera det högsta alternativet)</em></p>
        <v-chip-group active-class="primary--text" column v-model="educationAttainment">
            <v-chip value="Grundskola" outlined>Grundskola</v-chip>
            <v-chip value="Gymnasie" outlined>Gymnasie</v-chip>
            <v-chip value="Högskola" outlined>Högskola</v-chip>
        </v-chip-group>

        <h4 class="mb-2" :class="{surveyError: errors.includes('housingType')}">Bostadstyp</h4>
        <v-chip-group active-class="primary--text" column v-model="housingType">
            <v-chip value="Hyresrätt" outlined>Hyresrätt</v-chip>
            <v-chip value="Bostadsrätt" outlined>Bostadsrätt</v-chip>
            <v-chip value="Villa" outlined>Villa</v-chip>
            <v-chip value="Annat" outlined>Annat</v-chip>
        </v-chip-group>

        <h4 class="mb-2" :class="{surveyError: errors.includes('income')}">Ungefär hur stor är ditt hushålls totala inkomst före skatt en vanlig månad?</h4>
        <p class="mb-2"><em class="smaller-em">(Räkna med eventuella bidrag, t.ex. barnbidrag, bostadsbidrag, studiebidrag m.m.)</em></p>
        <v-chip-group active-class="primary--text" column v-model="income">
            <v-chip value="Under 20 000" outlined>Under 20 000</v-chip>
            <v-chip value="20 000 - 29 999" outlined>20 000 - 29 999</v-chip>
            <v-chip value="30 000 - 39 999" outlined>30 000 - 39 999</v-chip>
            <v-chip value="40 000 - 49 999" outlined>40 000 - 49 999</v-chip>
            <v-chip value="50 000 - 59 999" outlined>50 000 - 59 999</v-chip>
            <v-chip value="60 000 - 69 999" outlined>60 000 - 69 999</v-chip>
            <v-chip value="70 000 - 79 999" outlined>70 000 - 79 999</v-chip>
            <v-chip value="80 000 - 89 999" outlined>80 000 - 89 999</v-chip>
            <v-chip value="90 000 - 99 999" outlined>90 000 - 99 999</v-chip>
            <v-chip value="Över 100 000" outlined>Över 100 000</v-chip>
            <v-chip value="Vill inte ange" outlined>Vill inte ange</v-chip>
        </v-chip-group>

				<h4 class="mb-2" :class="{surveyError: errors.includes('email')}">{{ $processText('{email-address-input}') }} </h4>
				<p class="mb-2"><em class="smaller-em">Får vi be dig om råd och rekommendationer och fråga mer om dina resvanor om några månader? Stort tack i så fall! Ange i så fall din epost nedan.</em></p>
				<v-text-field dense hide-details placeholder="E-post" v-model="email" outlined></v-text-field>
				<br>
				Enkäten och dina personuppgifter hanteras av företaget Nudgd på uppdrag av Helsingborgs stad. Läs mer om <a :href="$processText('https://data.nudgd.se/{slug}')" target="_blank">hur dina personuppgifter hanteras.</a>. Lämnar du din mailadress här är dina svar tillfälligt inte längre anonyma, men vi kommer att radera den från din svarsdata inom 90 dagar. E-posten lagras dock i upp till ett år, men kommer endast användas för eventuella uppföljningsfrågor.

				<div class="mt-5">
                    <div v-if=" ! isNewUser">
                        <div class="nudgd-btn small survey-submit mt-3" @click="submitResponse()">Skicka in</div>
                    </div>

                    <div v-if="isNewUser">
                        <div style="display: inline-block;" class="nudgd-btn survey-submit mt-3" @click="submitResponse(true)">Skicka in och acceptera cookies</div>
                        <div class="nudgd-btn small survey-submit mt-3" @click="submitResponse()">Skicka in och avböj cookies</div>
                        <p class="mt-2"><em class="smaller-em"><a href="https://nudgd.se/om-nudgd/integritetspolicy/" target="_blank">Hur vi använder cookies och varför</a>. inte minst för att minimera risken att du behöver göra denna enkät igen när du går in nästa gång!</em></p>
                    </div>
				</div>

                <div class="nudgd-btn small survey-submit mt-0" @click="prevStep()">Tillbaka</div>

				<div style="clear:both;"></div>
			</div>

			<v-progress-linear color="#00A19A" height="10" rounded class="mt-8" :value="(stepIndex + 1) / (isNewUser ? steps : steps - 1) * 100"></v-progress-linear>

			<br><br>
			<p>Frågor? Helsingborgs stads <a href="https://helsingborg.se/kommun-och-politik/kontakt-och-paverkan/kontakta-kommunen/" target="_blank">kontaktcenter</a> finns här för dig.</p>
			<br>

			<div style="display:flex;">
				<a style="width:30%; margin-right:5%;" target="_blank" href="https://raptorproject.eu/"><img style="max-width:100%;" id="raptor-logo" class="hbg-footer-logo" src="https://www.dropbox.com/scl/fi/utuohojig7em00x34xhti/Raptor-logos_Blue_Horizontal.png?rlkey=7uhpzldlhb4b1f1ah0utvf94e&raw=1" /></a>
            	<a style="width:30%; margin-right:5%;" target="_blank" href="https://www.eiturbanmobility.eu/"><img style="max-width:100%;" id="eit-urban-mobility-logo" class="hbg-footer-logo" src="https://www.dropbox.com/scl/fi/yj3v6c6dg2pos2txuwc7i/eit-urban-mobility-logo.png?rlkey=aw7ja0iwut7koyauhbxs0hckx&raw=1" /></a>
            	<a style="width:30%;" target="_blank" href="https://www.eiturbanmobility.eu/"><img style="max-width:100%;" id="co-funded-by-the-eu-logo" class="hbg-footer-logo" src="https://www.dropbox.com/scl/fi/f200n29souasqkwzwzfht/logo-co-funded-eu.jpeg?rlkey=wczt3nyt5sizf93f2dhjo1cqo&raw=1" /></a>
			</div>

			<p class="mt-2 mb-0"><em class="smaller-em">
				The project is supported by EIT Urban Mobility, an initiative of the European Institute of Innovation and Technology (EIT), a body of the European Union. EIT Urban Mobility acts to accelerate prositive change on mobility to make urban spaces more liveable. Learn more: eiturbanmobility.eu
			</em></p>
		</div>
	</div>
</template>

<script>
import axios from 'axios'
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/default.css'
import ModeOfTransport from '@/components/ModeOfTransport.vue'

export default {
	components: {VueSlider, ModeOfTransport},

	mounted() {	
		this.uuid = Math.random().toString(36).substr(2, 5)
		this.$store.state.surveyId = 10

		// For testing purposes
		if (this.$route.query.page) {
			this.stepIndex = parseInt(this.$route.query.page)
		}

		// Try to get survey response
		axios.get(this.$apiUrl + '/api/survey/13/response/' + this.slug).then(res => {	
			const data = {...res.data.response, uuid: res.data.uuid}

			delete data.stepIndex;

			for (const prop in data) {
				this[prop] = data[prop]
			}
		})

		// Get end user
		if (this.slug) {
			axios.get(this.$apiUrl + '/api/end-user/' + this.slug + '?s=Pre-Survey').then(res => {
				this.$store.state.endUser = res.data.endUser
				this.$store.state.client = res.data.endUser.client
				this.$store.state.translations = res.data.translations
			})
		}

		if ( ! this.endUser.test_user && this.$getCookie('raptorCookiePost') && ! this.$route.query.fnp) {
			this.$router.push({name: 'EndUser', params: {slug: this.$getCookie('raptorCookiePost')}})
			return
		}
	},

	data() {
		return {
			steps: 5,

			mainModeOfTransport_POST: null,

			frequencyWalk_POST: null,
			frequencyBike_POST: null,
			frequencyTransit_POST: null,
			frequencyCar_POST: null,
			frequencyCarCoride_POST: null,
			frequencyMisc_POST: null,

			numberOfTrips_POST: null,

			weeklyWalking_POST: null,
			weeklyBike_POST: null,
			weeklyTransit_POST: null,
			weeklyCar_POST: null,
			weeklyCarCoride_POST: null,
			weeklyMisc_POST: null,

			weeklyElectricity_POST: null,
			weeklyHybrid_POST: null,
			weeklyGas_POST: null,
			
			otherTravelTypes_POST: null,


			mainModeOfTransport_PRE: null,

			frequencyWalk_PRE: null,
			frequencyBike_PRE: null,
			frequencyTransit_PRE: null,
			frequencyCar_PRE: null,
			frequencyCarCoride_PRE: null,
			frequencyMisc_PRE: null,

			numberOfTrips_PRE: null,

			weeklyWalking_PRE: null,
			weeklyBike_PRE: null,
			weeklyTransit_PRE: null,
			weeklyCar_PRE: null,
			weeklyCarCoride_PRE: null,
			weeklyMisc_PRE: null,

			weeklyElectricity_PRE: null,
			weeklyHybrid_PRE: null,
			weeklyGas_PRE: null,


			wishedIncreaseModeOfTransportWalk: null,
			wishedIncreaseModeOfTransportBike: null,
			wishedIncreaseModeOfTransportTransit: null,
			wishedIncreaseModeOfTransportCar: null,
			wishedIncreaseModeOfTransportCarSustainable: null,
			wishedIncreaseModeOfTransportCoride: null,
			othersWishedIncreaseModeOfTransportWalk: null,
			othersWishedIncreaseModeOfTransportBike: null,
			reasonForTransport_POST: null,
			reasonForTransportComment_POST: null,


			usedSmartNudges: null,
			smartNudgesEffect: null,
			more: {
				walking: null,
				biking: null,
				transit: null,
				car: null
			},
			usefulPartsOfPlatform: null,
			usefulPartsOfPlatformCommentary: null,
			smartNudgesRecommend: null,
			whyNotUsedPlatform: null,
			whyNotUsedPlatformCommentary: null,


			worksInSchool: null,
			hasChildrenInSchool: null,

			gender: null,
			birthYear: null,
			peopleInHousehold: null,
			noOfChildren: null,
			employment: null,
			distanceEmployment: null,
			educationAttainment: null,
			housingType: null,
			income: null,

			contactMe: null,
			email: null,


			firstStepConfirmed: false,
			firstStepConfirmedPre: false,
			errors: [],
			engaged: [],
			stepIndex: 0,
			thankYou: false,

			// Each array represents the required fields of a step
			required: [
				[
					'mainModeOfTransport_POST', 'frequencyWalk_POST', 'frequencyBike_POST',
					'frequencyTransit_POST', 'frequencyCar_POST', 'frequencyCarCoride_POST', 'frequencyMisc_POST'
				],
				[
					'mainModeOfTransport_PRE', 'frequencyWalk_PRE', 'frequencyBike_PRE',
					'frequencyTransit_PRE', 'frequencyCar_PRE', 'frequencyCarCoride_PRE', 'frequencyMisc_PRE'
					
				],
				[
					'wishedIncreaseModeOfTransportWalk', 'wishedIncreaseModeOfTransportBike',
					'wishedIncreaseModeOfTransportTransit', 'wishedIncreaseModeOfTransportCar',
					'wishedIncreaseModeOfTransportCarSustainable', 'wishedIncreaseModeOfTransportCoride',
					'othersWishedIncreaseModeOfTransportWalk','othersWishedIncreaseModeOfTransportBike',
					'reasonForTransport_POST'
				],
				[
					'usedSmartNudges', 
					'smartNudgesEffect',
					'usefulPartsOfPlatform', 
					'smartNudgesRecommend'
				],
				[]
			],
		}
	},

	watch: {
		mainModeOfTransport_POST(val) { this.engaged.push('mainModeOfTransport_POST') },
		frequencyWalk_POST(val) { this.engaged.push('frequencyWalk_POST') },
		frequencyBike_POST(val) { this.engaged.push('frequencyBike_POST') },
		frequencyTransit_POST(val) { this.engaged.push('frequencyTransit_POST') },
		frequencyCar_POST(val) { this.engaged.push('frequencyCar_POST') },
		frequencyCarCoride_POST(val) { this.engaged.push('frequencyCarCoride_POST') },
		frequencyMisc_POST(val) { this.engaged.push('frequencyMisc_POST') },
		numberOfTrips_POST(val) { this.engaged.push('numberOfTrips_POST') },

		weeklyWalking_POST(val) { this.engaged.push('weeklyWalking_POST') },
		weeklyBike_POST(val) { this.engaged.push('weeklyBike_POST') },
		weeklyTransit_POST(val) { this.engaged.push('weeklyTransit_POST') },
		weeklyCar_POST(val) { this.engaged.push('weeklyCar_POST') },
		weeklyCarCoride_POST(val) { this.engaged.push('weeklyCarCoride_POST') },
		weeklyMisc_POST(val) { this.engaged.push('weeklyMisc_POST') },
		weeklyElectricity_POST(val) { this.engaged.push('weeklyElectricity_POST') },
		weeklyHybrid_POST(val) { this.engaged.push('weeklyHybrid_POST') },
		weeklyGas_POST(val) { this.engaged.push('weeklyGas_POST') },

		otherTravelTypes_POST(val) { this.engaged.push('otherTravelTypes_POST') },

		mainModeOfTransport_PRE(val) { this.engaged.push('mainModeOfTransport_PRE') },
		frequencyWalk_PRE(val) { this.engaged.push('frequencyWalk_PRE') },
		frequencyBike_PRE(val) { this.engaged.push('frequencyBike_PRE') },
		frequencyTransit_PRE(val) { this.engaged.push('frequencyTransit_PRE') },
		frequencyCar_PRE(val) { this.engaged.push('frequencyCar_PRE') },
		frequencyCarCoride_PRE(val) { this.engaged.push('frequencyCarCoride_PRE') },
		frequencyMisc_PRE(val) { this.engaged.push('frequencyMisc_PRE') },
		numberOfTrips_PRE(val) { this.engaged.push('numberOfTrips_PRE') },

		weeklyWalking_PRE(val) { this.engaged.push('weeklyWalking_PRE') },
		weeklyBike_PRE(val) { this.engaged.push('weeklyBike_PRE') },
		weeklyTransit_PRE(val) { this.engaged.push('weeklyTransit_PRE') },
		weeklyCar_PRE(val) { this.engaged.push('weeklyCar_PRE') },
		weeklyCarCoride_PRE(val) { this.engaged.push('weeklyCarCoride_PRE') },
		weeklyMisc_PRE(val) { this.engaged.push('weeklyMisc_PRE') },
		weeklyElectricity_PRE(val) { this.engaged.push('weeklyElectricity_PRE') },
		weeklyHybrid_PRE(val) { this.engaged.push('weeklyHybrid_PRE') },
		weeklyGas_PRE(val) { this.engaged.push('weeklyGas_PRE') },

		otherTravelTypes_PRE(val) { this.engaged.push('otherTravelTypes_PRE') },

		wishedIncreaseModeOfTransportWalk(val) { this.engaged.push('wishedIncreaseModeOfTransportWalk') },
		wishedIncreaseModeOfTransportBike(val) { this.engaged.push('wishedIncreaseModeOfTransportBike') },
		wishedIncreaseModeOfTransportTransit(val) { this.engaged.push('wishedIncreaseModeOfTransportTransit') },
		wishedIncreaseModeOfTransportCar(val) { this.engaged.push('wishedIncreaseModeOfTransportCar') },
		wishedIncreaseModeOfTransportCarSustainable(val) { this.engaged.push('wishedIncreaseModeOfTransportCarSustainable') },
		wishedIncreaseModeOfTransportCoride(val) { this.engaged.push('wishedIncreaseModeOfTransportCoride') },
		othersWishedIncreaseModeOfTransportWalk(val) { this.engaged.push('othersWishedIncreaseModeOfTransportWalk') },
		othersWishedIncreaseModeOfTransportBike(val) { this.engaged.push('othersWishedIncreaseModeOfTransportBike') },
		reasonForTransport_POST(val) { this.engaged.push('reasonForTransport_POST') },
		reasonForTransportComment_POST(val) { this.engaged.push('reasonForTransportComment_POST') },
		
		usefulPartsOfPlatform(val) { this.engaged.push('usefulPartsOfPlatform') },
		
        usedSmartNudges(val) { this.engaged.push('usedSmartNudges') },
		smartNudgesEffect(val) { this.engaged.push('smartNudgesEffect') },
		employerNudging(val) { this.engaged.push('employerNudging') },
		smartNudgesRecommend(val) { this.engaged.push('smartNudgesRecommend') },
		'more.walking': function (val) { this.engaged.push('moreWalking') },
		'more.biking': function (val) { this.engaged.push('moreBiking') },
		'more.transit': function (val) { this.engaged.push('moreTransit') },
		'more.car': function (val) { this.engaged.push('moreCar') },
	
		worksInSchool(val) { this.engaged.push('worksInSchool') },
		hasChildrenInSchool(val) { this.engaged.push('hasChildrenInSchool') },
		contactMe(val) { this.engaged.push('contactMe') },


	},

	methods: {
		selectFrequency(property, value) {
			this[property] = value
		},

		selectModeOfTransport(mode) {
			this.mainModeOfTransport_POST = mode
		},

		selectModeOfTransportPRE(mode) {
			this.mainModeOfTransport_PRE = mode
		},

		// selectModeOfTransport(mode) {
		// 	this.MainModeOfTransport_PRE = mode
		// },

		confirmFirstStep() {
			if ( ! this.mainModeOfTransport_POST) {
				alert('Var god och välj ditt huvudsakliga färdsätt')
				return
			}

			this.firstStepConfirmed = true
		},

		confirmFirstStepPre() {
			if ( ! this.mainModeOfTransport_PRE) {
				alert('Var god och välj ditt huvudsakliga färdsätt')
				return
			}

			this.firstStepConfirmedPre = true
		},

		nextStep() {
			if ( ! this.validate()) {
				alert('Var god och fyll i alla rödmarkerade fält')
				return
			}

			window.scrollTo(0, 0)

			if (this.stepIndex === 0 && ! this.isNewUser) {
				this.stepIndex = this.stepIndex + 2;
			} else {
				this.stepIndex++
			}

			this.saveState()
		},

		validate() {
			this.errors = []

			let required = [...this.required[this.stepIndex]]

			if (this.stepIndex === 0) {
				if (parseInt(this.frequencyWalk_POST) > 0) { required.push('weeklyWalking_POST') }
				if (parseInt(this.frequencyBike_POST) > 0) { required.push('weeklyBike_POST') }
				if (parseInt(this.frequencyTransit_POST) > 0) { required.push('weeklyTransit_POST') }
				if (parseInt(this.frequencyCar_POST) > 0) { required.push('weeklyCar_POST') }
				if (parseInt(this.frequencyCarCoride_POST) > 0) { required.push('weeklyCarCoride_POST') }
				if (parseInt(this.frequencyMisc_POST) > 0) { required.push('weeklyMisc_POST') }

				if (this.frequencyWalk_POST || this.frequencyBike_POST || this.frequencyTransit_POST || this.frequencyCar_POST || this.frequencyCarCoride_POST || this.frequencyMisc_POST) {
					required.push('numberOfTrips_POST')
				}
			}

			if (this.stepIndex === 0) {
				// If weeklyCar_POST > 0, then the  type of fuels should add up
				if (this.weeklyCar_POST > 0 && (parseInt(this.weeklyElectricity_POST || 0) + parseInt(this.weeklyGas_POST || 0) + parseInt(this.weeklyHybrid_POST || 0) != this.weeklyCar_POST)) {
					alert('Summan av drivmedel måste bli ' + this.weeklyCar_POST)
					this.errors.push('weeklyElectricity_POST')
					this.errors.push('weeklyGas_POST')
					this.errors.push('weeklyHybrid_POST')
				}
			}

			if (this.stepIndex === 1) {
				if (parseInt(this.frequencyWalk_PRE) > 0) { required.push('weeklyWalking_PRE') }
				if (parseInt(this.frequencyBike_PRE) > 0) { required.push('weeklyBike_PRE') }
				if (parseInt(this.frequencyTransit_PRE) > 0) { required.push('weeklyTransit_PRE') }
				if (parseInt(this.frequencyCar_PRE) > 0) { required.push('weeklyCar_PRE') }
				if (parseInt(this.frequencyCarCoride_PRE) > 0) { required.push('weeklyCarCoride_PRE') }
				if (parseInt(this.frequencyMisc_PRE) > 0) { required.push('weeklyMisc_PRE') }

				if (this.frequencyWalk_PRE || this.frequencyBike_PRE || this.frequencyTransit_PRE || this.frequencyCar_PRE || this.frequencyCarCoride_PRE || this.frequencyMisc_PRE) {
					required.push('numberOfTrips_PRE')
				}
			}

			if (this.stepIndex === 1) {
				// If weeklyCar_PRE > 0, then the  type of fuels should add up
				if (this.weeklyCar_PRE > 0 && (parseInt(this.weeklyElectricity_PRE || 0) + parseInt(this.weeklyGas_PRE || 0) + parseInt(this.weeklyHybrid_PRE || 0) != this.weeklyCar_PRE)) {
					alert('Summan av drivmedel måste bli ' + this.weeklyCar_PRE)
					this.errors.push('weeklyElectricity_PRE')
					this.errors.push('weeklyGas_PRE')
					this.errors.push('weeklyHybrid_PRE')
				}
			}

			if (this.stepIndex === 4) {

				if (this.usedSmartNudges === 'Ja') {
					required = ['moreWalking', 'moreBiking', 'moreTransit', 'moreCar']
				}
				
				if (this.isNewUser) {
					if (this.isStaff) {
						required.push('hasChildrenInSchool')
					} else {
						required.push('worksInSchool')
					}
				}
			}

			// Remove most items if the person didn't use the service
			if (this.stepIndex === 3 && this.usedSmartNudges === 'Nej') {
				required = []
			}

			this.errors = required.filter(key => {
				return ! this.engaged.includes(key) || (this.$data[key] instanceof Array && this.$data[key].length === 0)
			})

			this.errors =  this.errors.concat(required.filter(val => ! this.engaged.includes(val)))

			return this.errors.length === 0
		},

		prevStep() {
			window.scrollTo(0, 0)

			if (this.stepIndex === 2 && ! this.isNewUser) {
				this.stepIndex = this.stepIndex - 2;
			} else {
				this.stepIndex--
			}
		},

		saveState() {
			axios.post(this.$apiUrl + '/api/survey/13', {
				...this.$data,
				uuid: this.uuid,
				slug: this.slug,
				seconds_passed: performance ? parseInt(performance.now() / 1000) : null,
				completed_pages: this.stepIndex + 1
			})
		},

		submitResponse(withCookie) {
			if ( ! this.validate()) {
				alert('Var god och fyll i alla rödmarkerade fält')
				return
			}

			if (withCookie) {
				this.$setCookie('raptorCookiePost', this.slug);
			}

			axios.post(this.$apiUrl + '/api/survey/13', {
				...this.$data,
				tagIds: this.endUser.tags?.length ? this.endUser.tags.map(t => {
					return t.id + '+' + t.title;
				}).join() : null,
				uuid: this.uuid,
				slug: this.slug,
				seconds_passed: performance ? parseInt(performance.now() / 1000) : null,
				completed_pages: this.stepIndex + 1,
				complete: true
			}).then(res => {
				this.$router.push({name: 'EndUser', params: {slug: this.endUser.slug}})
            })
		}
	},

	computed: {
		serviceImage() {
			return this.$store.state.client.customization?.service_img
		},

		hasCompletedPrevious() {
			return this.$store.state.endUser?.responses?.find(r => r.survey_id == 1 && r.complete)
		},

		isNewUser() {
			if (this.endUser.created_at) {
				return new Date(this.endUser.created_at).getTime() > 1701129600000;
			} else {
				return true
			}
		},

        isStaff() {
			return this.endUser?.tags?.find(t => t.id == 1347);
		},

		isParent() {
			return this.endUser?.tags?.find(t => t.id == 1346);
		},
		
		slug() {
			// Cookie slug precedence
			return this.$route.params.slug || this.endUser.slug || this.$getCookie('raptorCookie');
		},

		endUser() {
			return this.$store.state.endUser
		},

		weeklySum_POST() {
			return parseInt(this.weeklyWalking_POST || 0) + parseInt(this.weeklyBike_POST || 0) + parseInt(this.weeklyTransit_POST || 0) + parseInt(this.weeklyCar_POST || 0) + parseInt(this.weeklyCarCoride_POST || 0) + parseInt(this.weeklyMisc_POST || 0)
		},

		computedWeeklyAverage_POST() {
			if ( ! this.numberOfTrips_POST) {
				return 0
			}

			return (this.weeklySum_POST / this.numberOfTrips_POST).toFixed(0).replace('.', ',')
		},

		yearlyDistance_POST() {
			return this.weeklySum_POST * 52
		},
        
		weeklySum_PRE() {
			return parseInt(this.weeklyWalking_PRE || 0) + parseInt(this.weeklyBike_PRE || 0) + parseInt(this.weeklyTransit_PRE || 0) + parseInt(this.weeklyCar_PRE || 0) + parseInt(this.weeklyCarCoride_PRE || 0) + parseInt(this.weeklyMisc_PRE || 0)
		},

		computedWeeklyAverage_PRE() {
			if ( ! this.numberOfTrips_PRE) {
				return 0
			}

			return (this.weeklySum_PRE / this.numberOfTrips_PRE).toFixed(0).replace('.', ',')
		},

		yearlyDistance_PRE() {
			return this.weeklySum_PRE * 52
		},
    apiUrl() {
      return this.$apiUrl
    },
	}
}
</script>
